import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Group, Select, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Alert, Button, PageHeader, SectionCard, StatusBadge } from "@xplatform/design-system";
import { IconHistory } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { AuditEntry, AuditFilters } from "@models/access";
import { AdminTable, col } from "@components/AdminTable";
import { useAudit } from "@hooks/access";
import { AuditDetailModal, EnvironmentBanner, auditActor, auditTarget } from "@features/Access";

/** Actions core records; the filter also offers any other action it has seen. */
const KNOWN_ACTIONS = [
  "user.create", "user.access", "user.update", "role.create", "role.update", "role.delete",
  "permissions.plan.create", "permissions.plan.step", "permissions.plan.cancel", "encryption.rotation",
];

/**
 * Every change made through access management (and the key rotation), newest
 * first. Entries hold ids only; names are resolved when the page is read. An
 * intent whose result never came back is shown as such, never hidden.
 */
export const AuditPage = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [cursors, setCursors] = useState<string[]>([]);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  // The URL keeps the picked days; core's `to` is exclusive, so the last day is sent as the day after.
  const to = params.get("to");
  const filters: AuditFilters = {
    from: params.get("from") ?? undefined,
    to: to ? dayjs(to).add(1, "day").format("YYYY-MM-DD") : undefined,
    actorId: params.get("actorId") ?? undefined,
    action: params.get("action") ?? undefined,
    cursor: cursors[cursors.length - 1],
  };
  const audit = useAudit(filters);
  useEffect(() => setCursors([]), [filters.from, filters.to, filters.actorId, filters.action]);

  const set = (patch: Record<string, string | null>) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: true });

  const items = useMemo(() => audit.data?.items ?? [], [audit.data]);
  const actors = useMemo(() => {
    const seen = new Map<string, string>();
    items.forEach((e) => e.actorId && seen.set(e.actorId, auditActor(e)));
    if (filters.actorId && !seen.has(filters.actorId)) seen.set(filters.actorId, filters.actorId);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [items, filters.actorId]);
  const actions = useMemo(
    () => [...new Set([
      ...KNOWN_ACTIONS,
      ...items.flatMap((e) => (e.action ? [e.action] : [])),
      ...(filters.action ? [filters.action] : []),
    ])],
    [items, filters.action],
  );

  const range: [Date | null, Date | null] = [
    filters.from ? dayjs(filters.from).toDate() : null,
    to ? dayjs(to).toDate() : null,
  ];

  const columns = [
    col.date<AuditEntry>("at", t("admin.access.audit.columns.time"), (e) => e.at),
    col.text<AuditEntry>("actor", t("admin.access.audit.columns.admin"), auditActor, { width: 200 }),
    col.text<AuditEntry>("action", t("admin.access.audit.columns.action"), (e) => e.action ?? "-", { width: 180, mono: true }),
    col.twoLine<AuditEntry>("target", t("admin.access.audit.columns.target"), (e) => {
      const target = auditTarget(e);
      return { primary: target.name ?? target.id, secondary: target.type };
    }),
    col.badge<AuditEntry>("result", t("admin.access.audit.columns.result"), (e) =>
      e.result === null
        ? <Text size="sm" c="orange.8">{t("admin.access.audit.noResult")}</Text>
        : <StatusBadge size="sm" status={e.result} tone={e.result === "ok" ? "success" : "error"} />, 160),
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.access.audit.title")}
        description={t("admin.access.audit.description")}
        icon={<IconHistory size={28} />}
      />
      <EnvironmentBanner />

      <SectionCard
        actions={
          <Group gap="sm" wrap="wrap">
            <DatePickerInput
              type="range"
              size="sm"
              w={260}
              clearable
              value={range}
              maxDate={new Date()}
              aria-label={t("admin.access.audit.filters.range")}
              placeholder={t("admin.access.audit.filters.range")}
              onChange={([from, to]) => {
                if (from && to) set({ from: dayjs(from).format("YYYY-MM-DD"), to: dayjs(to).format("YYYY-MM-DD") });
                else if (!from && !to) set({ from: null, to: null });
              }}
              popoverProps={{ portalProps: { target: "#admin-portal-root" } }}
            />
            <Select
              aria-label={t("admin.access.audit.filters.admin")}
              placeholder={t("admin.access.audit.filters.admin")}
              data={actors}
              value={filters.actorId ?? null}
              onChange={(v) => set({ actorId: v })}
              clearable
              searchable
              w={200}
              comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
            />
            <Select
              aria-label={t("admin.access.audit.filters.action")}
              placeholder={t("admin.access.audit.filters.action")}
              data={actions}
              value={filters.action ?? null}
              onChange={(v) => set({ action: v })}
              clearable
              w={200}
              comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
            />
          </Group>
        }
      >
        <Stack gap="sm">
          {audit.error && <Alert color={audit.forbidden ? "gray" : "red"}>{audit.error}</Alert>}
          <AdminTable
            columns={columns}
            data={items}
            getRowKey={(e) => e.id}
            onRowClick={setSelected}
            loading={audit.loading}
            emptyMessage={t("admin.access.audit.empty")}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" size="xs" disabled={cursors.length === 0}
                    onClick={() => setCursors(cursors.slice(0, -1))}>
              {t("admin.access.audit.newer")}
            </Button>
            <Button variant="subtle" size="xs" disabled={!audit.data?.nextCursor}
                    onClick={() => audit.data?.nextCursor && setCursors([...cursors, audit.data.nextCursor])}>
              {t("admin.access.audit.older")}
            </Button>
          </Group>
        </Stack>
      </SectionCard>

      <AuditDetailModal entry={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
};
